'use client';

import React, { useState } from 'react';
import { Box } from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import OptimizedImage from './OptimizedImage';

interface VideoThumbnailProps {
    videoUrl: string;
    thumbnailUrl?: string; // Optional custom thumbnail
    alt?: string;
    fill?: boolean;
    onClick?: () => void;
    priority?: boolean;
    sizes?: string;
}

const VideoThumbnail: React.FC<VideoThumbnailProps> = ({
    videoUrl,
    thumbnailUrl,
    alt = 'Video thumbnail',
    fill = true,
    onClick,
    priority = false,
    sizes = '100vw',
}) => {
    const [isHovered, setIsHovered] = useState(false);

    // If no thumbnailUrl is provided, use a default gradient background
    const backgroundStyle = thumbnailUrl ? {} : {
        background: 'linear-gradient(135deg, #1C1C34 0%, #474787 100%)',
    };

    return (
        <Box
            className="video-thumbnail relative group cursor-pointer"
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            sx={{
                width: fill ? '100%' : 'auto',
                height: fill ? '100%' : 'auto',
                position: 'relative',
                aspectRatio: fill ? undefined : '16/9',
                borderRadius: '8px',
                overflow: 'hidden',
                ...backgroundStyle,
            }}
        >
            {thumbnailUrl && (
                <OptimizedImage
                    src={thumbnailUrl}
                    alt={alt}
                    fill={fill}
                    sizes={sizes}
                    priority={priority}
                    className="object-cover transition-opacity duration-300"
                />
            )}

            {/* Play button overlay */}
            <Box
                className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 transition-all"
                sx={{
                    opacity: isHovered ? 0.7 : 0.5,
                }}
            >
                <PlayCircleOutlineIcon
                    className="text-white transform transition-transform duration-300"
                    sx={{
                        fontSize: { xs: '3rem', sm: '4rem' },
                        transform: isHovered ? 'scale(1.2)' : 'scale(1)',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.5))',
                    }}
                />
            </Box>
        </Box>
    );
};

export default VideoThumbnail; 